'use client';

/**
 * GoCardless Drop-in Component
 *
 * Renders an embedded payment modal using GoCardless's Drop-in UI.
 * Uses the vanilla JS SDK since @gocardless/react-dropin doesn't support React 19.
 *
 * Supports:
 * - Custom return URL redirect after successful payment
 * - PostMessage communication for iframe embed mode
 */

import { useEffect, useRef, useCallback } from 'react';
import Script from 'next/script';
import { postMessageToParent, isInIframe } from '@/lib/iframe/post-message';
import { saveRedirectState } from '@/lib/iframe/redirect-state';

// Extend window type to include GoCardlessDropin
declare global {
  interface Window {
    GoCardlessDropin?: {
      create: (options: GoCardlessDropinOptions) => GoCardlessDropinHandler;
    };
  }
}

interface GoCardlessDropinOptions {
  billingRequestFlowID: string;
  environment: 'live' | 'sandbox';
  onSuccess: (billingRequest: unknown, billingRequestFlow: unknown) => void;
  onExit: (error: unknown | null, metadata: unknown) => void;
}

interface GoCardlessDropinHandler {
  open: () => void;
  exit: () => void;
}

interface GoCardlessDropinProps {
  billingRequestFlowId: string;
  donationId: string;
  customReturnUrl?: string | null;
  isEmbed?: boolean;
  onSuccess?: () => void;
  onExit?: (error?: string) => void;
}

export function GoCardlessDropin({
  billingRequestFlowId,
  donationId,
  customReturnUrl,
  isEmbed = false,
  onSuccess: onSuccessCallback,
  onExit,
}: GoCardlessDropinProps) {
  const handlerRef = useRef<GoCardlessDropinHandler | null>(null);
  const hasOpenedRef = useRef(false);
  // Latest onSuccess, read through a ref so a parent passing a new inline
  // callback each render doesn't re-create initDropin (and re-open the modal).
  const onSuccessRef = useRef(onSuccessCallback);
  useEffect(() => {
    onSuccessRef.current = onSuccessCallback;
  }, [onSuccessCallback]);

  const environment = (process.env.NEXT_PUBLIC_GOCARDLESS_ENVIRONMENT || 'sandbox') as 'live' | 'sandbox';

  const initDropin = useCallback(() => {
    if (!window.GoCardlessDropin || hasOpenedRef.current) return;

    hasOpenedRef.current = true;

    handlerRef.current = window.GoCardlessDropin.create({
      billingRequestFlowID: billingRequestFlowId,
      environment,
      onSuccess: () => {
        // Set paymentComplete → the donation form renders its in-page
        // thank-you card with annual/one-off-specific messaging + a manual
        // "back to homepage" link. We DON'T navigate away — the donor needs
        // time to read the confirmation. For the popup path (one-off desktop),
        // the popup's success page auto-closes itself separately.
        onSuccessRef.current?.();

        if (isEmbed && isInIframe()) {
          postMessageToParent({
            type: 'donation_success',
            donationId,
          });
        }
      },
      onExit: (error) => {
        if (error) {
          console.error('GoCardless Drop-in error:', error);
          const errorMessage = typeof error === 'object' && error !== null && 'message' in error
            ? (error as { message: string }).message
            : 'Payment was cancelled or failed';

          // Notify parent if in embed mode
          if (isEmbed && isInIframe()) {
            postMessageToParent({
              type: 'donation_error',
              donationId,
              error: errorMessage,
            });
          }

          onExit?.(errorMessage);
        } else {
          // User exited voluntarily
          if (isEmbed && isInIframe()) {
            postMessageToParent({
              type: 'donation_cancelled',
              donationId,
            });
          }
          onExit?.();
        }
      },
    });

    // Save redirect state before opening - bank redirect will break iframe context
    // This allows us to handle the return properly
    saveRedirectState({
      donationId,
      charityId: '', // Not needed for return handling
      isEmbed,
      customReturnUrl: customReturnUrl || undefined,
    });

    // Notify parent that redirect is starting (they may lose iframe context)
    if (isEmbed && isInIframe()) {
      postMessageToParent({
        type: 'donation_redirect_starting',
        donationId,
      });
    }

    // Open the modal immediately
    handlerRef.current.open();
  }, [billingRequestFlowId, donationId, environment, onExit, customReturnUrl, isEmbed]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (handlerRef.current) {
        handlerRef.current.exit();
      }
    };
  }, []);

  return (
    <Script
      src="https://pay.gocardless.com/billing/static/dropin/v2/initialise.js"
      strategy="afterInteractive"
      onLoad={initDropin}
    />
  );
}
